import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import {
  SpeechBubble,
  EditQuestionIcon,
  EyeOpenIcon,
  EyeClosedIcon
} from 'assets/svgs';
import { Button } from 'components';
import { Markup } from 'interweave';
import { transform } from 'motion';
import { FirebaseContext } from 'context';
import Question from '../Question';
import ToolbarDropdown from '../ToolbarDropdown';

function Questions({
  questions,
  pendingQuestionId,
  isCancelling,
  setSelectedQuestion,
  firebase,
  sessionId,
  handleEditQuestion,
  handleToggleQuestionVisibility,
  isModerator,
  uid,
  editingQuestion,
  editorState,
  setEditorState,
  isEditorReady,
  Editor,
  editorRef,
  editorIsFocused,
  setEditorIsFocused,
  editorStyle,
  toolbarStyle,
  getToolbarOptions,
  width,
  editorContent,
  handleCancelEdit,
  handleSaveEdit,
  uploadImageCallback,
  localization
}) {
  const [sessionComments, setSessionComments] = useState([]);
  const { user } = useContext(FirebaseContext);

  useEffect(() => {
    let unsubscribeToAllSessionComments;
    if (firebase && sessionId) {
      unsubscribeToAllSessionComments =
        firebase.questions.subscribeToAllSessionComments({
          sessionId,
          snapshot: (snapshot) => {
            const _sessionComments = [];
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                _sessionComments.push({
                  id: doc.id,
                  ...doc.data()
                });
              });
            }
            setSessionComments(_sessionComments);
          }
        });
    }
    return () => {
      if (unsubscribeToAllSessionComments) {
        unsubscribeToAllSessionComments();
      }
    };
  }, [firebase, sessionId]);

  return (
    <Container layout transition={isCancelling ? undefined : { duration: 0 }}>
      {questions
        .filter((q) => q.questionId !== pendingQuestionId)
        .sort((a, b) =>
          sessionId === 'hl1t9c' ? a.order - b.order : b.order - a.order
        )
        .map((question) => {
          const comments = sessionComments.filter(
            (_comment) => _comment.questionId === question.id
          );
          const myComments = comments.filter(
            (_comment) => _comment.uid === uid
          );
          return (
            <Question
              key={question.id}
              layout
              transition={isCancelling ? undefined : { duration: 0 }}
              question={question}
              editingQuestion={editingQuestion?.id === question.id}
              isHidden={!question.isVisible}
              setSelectedQuestion={setSelectedQuestion}
              setEditorIsFocused={setEditorIsFocused}
              markupContent={question.html}>
              {editingQuestion?.id === question.id ? (
                <>
                  {isEditorReady && Editor && (
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={setEditorState}
                      wrapperClassName="react-draft-wrapper"
                      toolbarClassName="react-draft-toolbar"
                      editorClassName="react-draft-editor"
                      ref={editorRef}
                      onFocus={() => setEditorIsFocused(true)}
                      onBlur={() => setEditorIsFocused(false)}
                      toolbar={getToolbarOptions({
                        uploadCallback: uploadImageCallback
                      })}
                      toolbarCustomButtons={
                        width < 550
                          ? [
                              <ToolbarDropdown
                                editorState={editorState}
                                onChange={setEditorState}
                              />
                            ]
                          : []
                      }
                      editorStyle={editorStyle({
                        editorIsFocused,
                        addingQuestion: false
                      })}
                      toolbarStyle={toolbarStyle}
                      placeholder="Type your question here"
                      localization={localization}
                      stripPastedStyles
                    />
                  )}
                  <EditQuestionButtonWrapper>
                    <Button
                      onClick={handleCancelEdit}
                      backgroundColor="var(--red)">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} disabled={!editorContent}>
                      Save
                    </Button>
                  </EditQuestionButtonWrapper>
                </>
              ) : (
                <>
                  <Markup
                    content={question.html}
                    tagName="div"
                    className="markup"
                  />
                  <Comments
                    $myComments={myComments}
                    $isHidden={!question.isVisible}>
                    {sessionId === 'x0ffd4' && (
                      <div>
                        <SpeechBubble />
                        {comments.length} Comment
                        {comments.length === 1 ? '' : 's'}
                      </div>
                    )}
                    <div>
                      <SpeechBubble />
                      {myComments.length} My Comments
                    </div>
                  </Comments>
                  <ButtonWrapper>
                    <CommentButtonWrapper $isHidden={!question.isVisible}>
                      <Button onClick={() => setSelectedQuestion(question)}>
                        Comment
                      </Button>
                    </CommentButtonWrapper>
                    {isModerator && (
                      <ModeratorControls>
                        {user?.email.includes('agencyx.ie') && (
                          <Button
                            $variant={width < 600 ? 'contained' : 'text'}
                            onClick={() =>
                              handleToggleQuestionVisibility(
                                question.questionId,
                                question.isVisible
                              )
                            }
                            icon={
                              question.isVisible ? (
                                <EyeOpenIcon
                                  width="1.25rem"
                                  style={{
                                    transform:
                                      width < 600
                                        ? 'scale(1.125)'
                                        : 'scale(1.25) translateX(1px)'
                                  }}
                                />
                              ) : (
                                <EyeClosedIcon
                                  width="1.25rem"
                                  style={{
                                    transform:
                                      width < 600
                                        ? 'scale(1.125)'
                                        : 'scale(1.25) translateX(1px)'
                                  }}
                                />
                              )
                            }
                            iconPosition="right">
                            {width > 600 && question.isVisible
                              ? 'Question Visible'
                              : width > 600 && !question.isVisible
                                ? 'Question Hidden'
                                : ''}
                          </Button>
                        )}
                        <Button
                          $variant={width < 600 ? 'contained' : 'text'}
                          onClick={() => handleEditQuestion(question)}
                          icon={
                            <EditQuestionIcon
                              width="1.25rem"
                              style={
                                width < 600
                                  ? {
                                      transform:
                                        'translateX(1px) translateY(-1px)'
                                    }
                                  : { transform: 'translateY(-1px)' }
                              }
                            />
                          }
                          iconPosition="right">
                          {width > 600 ? 'Edit Question' : ''}
                        </Button>
                      </ModeratorControls>
                    )}
                  </ButtonWrapper>
                </>
              )}
            </Question>
          );
        })}
    </Container>
  );
}

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Comments = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 1.1rem 0 1rem;
  max-width: 17.188rem;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.5 : 1)};

  > div {
    align-items: center;
    color: var(--blue);
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 120%;

    svg {
      margin-right: 0.438rem;
      width: 1.063rem;
    }

    &:last-of-type {
      color: ${({ $myComments }) =>
        $myComments.length ? 'var(--blue)' : 'var(--red)'};

      svg {
        transform: rotate(180deg);

        path {
          fill: ${({ $myComments }) =>
            $myComments.length ? 'var(--blue)' : 'var(--red)'};
        }
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  button {
    .button-text {
      transform: translateY(1px);
    }
  }
`;

const CommentButtonWrapper = styled.div`
  opacity: ${({ $isHidden }) => ($isHidden ? 0.5 : 1)};
`;

const ModeratorControls = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;

  button {
    span {
      gap: 0.7em;
    }

    @media (max-width: 599px) {
      width: 40px;
      height: 40px;

      path {
        fill: #fff;
      }
    }

    @media (min-width: 600px) {
      &:first-of-type {
        left: 30px;
      }

      &:last-of-type {
        left: 15px;
      }
    }
  }
`;

const EditQuestionButtonWrapper = styled.div`
  bottom: 1.188rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  position: absolute;
  right: 1.188rem;
  z-index: 1;
`;

export default Questions;
